import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, h1, h6, paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CalculatorResponseStyles from '@stories/Components/CalculatorResponse/CalculatorResponse.styles';
import FiltersFormContainerStyles from '@stories/Components/Misc/FiltersFormContainer/FiltersFormContainer.styles';
import FormStyles, { CommonLegendStyles } from '@stories/Components/Misc/Forms/Form.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
import BuyToLetRentalIncomeCalculatorStyles from '../BuyToLetRentalIncomeCalculator/BuyToLetRentalIncomeCalculator.styles';
import SavingsProductFinderStyles from '../SavingsProductFinder/SavingsProductFinder.styles';
const Container = styled(SavingsProductFinderStyles.Container) `
  ${FiltersFormContainerStyles.FormWrapper} {
    background-color: ${brand.global.white};
    border: 1px solid ${brand.grey.light};

    @container filters-form-container ${until(675)} {
      border-top: 0;
    }

    @container filters-form-container ${from(675)} {
      border-left: 0;
    }
  }

  ${CalculatorResponseStyles.Container} {
    position: sticky;
    bottom: 0;
    background: ${brand.global.white};
    padding-bottom: 16px;
    margin-bottom: -16px;
    z-index: 2;
  }
`;
const TabsWrapper = styled.div `
  --markerSize: ${fluid(36, 40)};

  position: relative;
  grid-column: 1 / -1;
  display: flex;
  gap: 20px;
  margin: 0 0 ${fluid(32, 40)};
  justify-content: space-between;

  &::before {
    ${transition('background-color')};

    content: '';
    position: absolute;
    left: ${fluid(32, 64, Device.MobileLarge, Device.Tablet)};
    right: ${fluid(32, 64, Device.MobileLarge, Device.Tablet)};
    top: calc(var(--markerSize) / 2);
    transform: translateY(-50%);
    height: 1px;
    background-color: ${brand.green.light};
    z-index: -1;
  }

  &[data-complete='true']::before {
    background-color: ${brand.green.dark};
  }

  @media ${from(Device.Tablet)} {
    justify-content: flex-start;
    margin-left: auto;
    margin-right: auto;
  }
`;
const StepTab = styled.button `
  ${ButtonReset};
  ${paragraphSmall};
  ${fonts.proximaNova.bold};

  display: grid;
  justify-items: center;
  grid-template-rows: min-content auto;
  gap: 8px;
  margin: 0;
  color: ${brand.blue.dark};
  flex: 1 1 126px;

  &:disabled {
    cursor: not-allowed;
  }

  @media ${until(Device.Tablet)} {
    span {
      ${srOnly};
    }
  }
`;
const StepTabNumber = styled.div `
  ${transition('background-color, color')};

  display: grid;
  place-content: center;
  width: var(--markerSize);
  aspect-ratio: 1;
  border: 1px solid ${brand.green.dark};
  color: ${brand.green.dark};
  font-size: 15px;
  border-radius: 5000px;

  ${StepTab}[data-stage='complete'] & {
    background-color: ${brand.green.dark};
    color: ${brand.global.white};
  }

  ${StepTab}[data-stage='active'] &,
  ${StepTab}[data-stage='complete']:hover &,
  ${StepTab}[data-stage='complete']:focus-visible & {
    background-color: ${brand.green.light};
    color: ${brand.green.dark};
  }

  ${StepTab}:focus-visible & {
    outline: auto;
    outline-offset: 5px;
    outline-width: 2px;
  }

  ${StepTab}:disabled & {
    background-color: ${brand.global.white};
    border-color: ${brand.green.light};
  }
`;
const FormWrapper = styled(BuyToLetRentalIncomeCalculatorStyles.FormWrapper) ``;
const FormSectionAccordionTitle = styled(FormStyles.Title) `
  button {
    ${ButtonReset};
    ${CommonLegendStyles};

    display: flex;
    white-space: nowrap;
    text-align: left;
    padding: 0;
    margin: 0 !important;
    align-items: center;
    gap: ${fluid(12, 16)};
    border: 0;

    svg {
      ${transition('transform')};

      display: block;
      width: ${fluid(12, 16)};
      color: ${brand.green.dark};
    }

    &[aria-expanded='true'] svg {
      transform: rotate(180deg);
    }
  }
`;
const AccordionFormFields = styled(m.div) `
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${fluid(20, 24)};
`;
const ButtonsWrapper = styled.div `
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  ${ButtonStyles.Container}[data-type='tertiary'] {
    margin-left: auto;
  }

  /* Styles for back button arrow */
  ${ButtonStyles.Container}[data-type='default'][data-icon-type='arrow-right'] {
    ${ButtonStyles.IconWrapper} {
      transform: scale(-1);
    }

    &:hover,
    &:focus-visible {
      ${ButtonStyles.IconWrapper} {
        transform: scale(-1) translateX(4px);
      }
    }
  }

  @media ${until(Device.Tablet)} {
    /* Target the scroll to products button */
    ${ButtonStyles.Container}[data-type='tertiary'][href] {
      order: -1;
      margin-left: 0;
    }
  }
`;
const ProductCards = styled(BuyToLetRentalIncomeCalculatorStyles.ProductCards) ``;
const CompleteText = styled.p `
  ${h6};

  margin: 0;

  &:not(:first-child) {
    margin-top: ${fluid(24, 32)};
  }
`;
const BorrowAmount = styled.p `
  ${h1};

  color: ${brand.green.default};
  margin: ${fluid(8, 32)} 0 0;
`;
const Disclaimer = styled.p `
  ${contentStyles};

  margin: ${fluid(16, 32)} 0 0;

  p {
    ${paragraphSmall};
    ${fonts.proximaNova.medium};
  }
`;
const AffordabilityCalculatorStyles = {
    AccordionFormFields,
    BorrowAmount,
    ButtonsWrapper,
    CompleteText,
    Container,
    Disclaimer,
    FormSectionAccordionTitle,
    FormWrapper,
    ProductCards,
    StepTab,
    StepTabNumber,
    TabsWrapper,
};
export default AffordabilityCalculatorStyles;
